import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api, { importAPI, employeesAPI, departmentsAPI } from '../services/api';
import IntervalPicker from '../components/IntervalPicker';
import { canEditOwnIPField, canEditAnyIP } from '../utils/ipPermissions';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge, { RoleBadge } from '../components/ui/Badge';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Input from '../components/ui/Input';
import SearchableSelect from '../components/ui/SearchableSelect';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import ConfirmModal from '../components/ui/ConfirmModal';
import Skeleton from '../components/ui/Skeleton';
import { useToast } from '../context/ToastContext';

const PAGE_SIZE = 25;

const Employees = ({ scopedCompanyId }) => {
  const { t } = useTranslation();
  const { user, impersonate } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const embedded = Boolean(scopedCompanyId);
  const companyIdFilter = scopedCompanyId || searchParams.get('companyId');
  // { id, mode: 'delete' | 'deactivate' | 'activate' }
  const [actionTarget, setActionTarget] = useState(null);

  // SSM-iștii (SUPER_ADMIN, SSM_COMPANY_ADMIN, SSM_INSTRUCTOR) gestionează MULTIPLE firme client,
  // deci /employees fără o firmă selectată îi trimite la lista de firme să aleagă una.
  // Rolurile interne (COMPANY_ADMIN, DIRECTOR_GENERAL) și angajații cu drepturi (HR/CLM)
  // aparțin UNEI singure firme — pentru ei /employees fără query afișează direct angajații
  // propriei firme (backend-ul deduce firma din token), fără redirect.
  const isSSMManagerRole = ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(
    user?.role
  );

  useEffect(() => {
    if (!embedded && !companyIdFilter && !searchParams.get('tab') && isSSMManagerRole) {
      navigate('/companies', { replace: true });
    }
  }, [embedded, companyIdFilter, searchParams, navigate, isSSMManagerRole]);
  const [deleting, setDeleting] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  // Firma vizată chiar are departamente? Dacă nu, angajații fără departament NU sunt „nealocați"
  // (nu au unde să fie alocați) — sunt implicit la nivel de firmă, fără avertisment.
  const [companyHasDepartments, setCompanyHasDepartments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Filtre suplimentare
  const [selectedCompany, setSelectedCompany] = useState(companyIdFilter || '');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedJobTitle, setSelectedJobTitle] = useState('');
  const [onlyEPI, setOnlyEPI] = useState(false);
  // Bulk import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);

  // Bulk selection pentru intervale IP — modul de selecție e ascuns by default
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkIPModalOpen, setBulkIPModalOpen] = useState(false);
  const [bulkSSM, setBulkSSM] = useState(null);
  const [bulkSU, setBulkSU] = useState(null);
  const [bulkApplySSM, setBulkApplySSM] = useState(false);
  const [bulkApplySU, setBulkApplySU] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  const enterSelectionMode = () => {
    setSelectionMode(true);
    setSelectedIds(new Set());
  };
  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const togglePageSelection = (ids, allChecked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allChecked) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };
  const submitBulkIP = async () => {
    if (!bulkApplySSM && !bulkApplySU) {
      showToast(t('employees.selectAtLeastOne'), 'warning');
      return;
    }
    setBulkSaving(true);
    try {
      const payload = { employeeIds: Array.from(selectedIds) };
      if (bulkApplySSM) payload.ipSSMIntervalDays = bulkSSM;
      if (bulkApplySU) payload.ipSUIntervalDays = bulkSU;
      const res = await employeesAPI.bulkUpdateIPIntervals(payload);
      showToast(t('employees.updatedCount', { count: res.data.updated }), 'success');
      setBulkIPModalOpen(false);
      exitSelectionMode();
      setBulkApplySSM(false);
      setBulkApplySU(false);
      setBulkSSM(null);
      setBulkSU(null);
      fetchEmployees();
    } catch (err) {
      showToast(err.response?.data?.error || t('employees.updateError'), 'error');
    } finally {
      setBulkSaving(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompany, selectedDepartment, selectedJobTitle, onlyEPI]);

  const isSSMRole = ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role);
  const canManageIP = canEditAnyIP(user);
  const canEditSSMField = canEditOwnIPField(user, 'ssm');
  const canEditSUField = canEditOwnIPField(user, 'su');
  const companyLocked = !!companyIdFilter;
  // „Nealocat" se arată doar dacă firma vizată chiar are departamente. În lista globală
  // (fără firmă selectată) păstrăm comportamentul vechi.
  const showUnassigned = !(companyIdFilter || selectedCompany) || companyHasDepartments;

  useEffect(() => {
    fetchEmployees();
  }, [companyIdFilter, selectedCompany]);

  // Aflăm dacă firma vizată are departamente — controlează afișarea badge-ului „Nealocat".
  useEffect(() => {
    const cid = companyIdFilter || selectedCompany;
    if (!cid) {
      setCompanyHasDepartments(false);
      return;
    }
    departmentsAPI
      .getAll(cid)
      .then((res) => setCompanyHasDepartments((res.data || []).length > 0))
      .catch(() => setCompanyHasDepartments(false));
  }, [companyIdFilter, selectedCompany]);

  useEffect(() => {
    if (isSSMRole) {
      api
        .get('/companies')
        .then((res) => setCompanies((res.data || []).filter((c) => c.type === 'CLIENT')))
        .catch(() => setCompanies([]));
    }
  }, [isSSMRole]);

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const targetCompany = companyIdFilter || selectedCompany;
      const url = targetCompany ? `/employees?companyId=${targetCompany}` : '/employees';
      const response = await api.get(url);
      setEmployees(response.data);

      if (targetCompany && response.data.length > 0) {
        setCompanyName(response.data[0].company?.name || '');
      } else if (!targetCompany) {
        setCompanyName('');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || t('employees.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const targetCompany = companyIdFilter || selectedCompany || user?.companyId;
      if (targetCompany) fd.append('companyId', targetCompany);
      const res = await importAPI.uploadEmployees(fd);
      setImportResult(res.data);
      fetchEmployees();
    } catch (err) {
      setImportResult({ error: err.response?.data?.error || t('employees.importError') });
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await importAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_angajati.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast(t('employees.templateDownloadError'), 'error');
    }
  };

  // Export CSV client-side al listei FILTRATE de angajați. Dacă suntem în mod selecție și
  // există rânduri bifate, exportăm doar selecția; altfel toți angajații filtrați curent.
  // CNP-ul NU e inclus (e criptat și nu vine în payload-ul listei — date sensibile GDPR).
  const handleExportCSV = () => {
    const source =
      selectionMode && selectedIds.size > 0
        ? filteredEmployees.filter((e) => selectedIds.has(e.id))
        : filteredEmployees;
    if (source.length === 0) {
      showToast(t('employees.exportEmpty'), 'warning');
      return;
    }
    const headers = [
      t('employees.colName'),
      t('employees.colEmail'),
      t('employees.colJobRole'),
      t('employees.colDept'),
      t('employees.colCompany'),
    ];
    // Escape CSV: dublăm ghilimelele și încadrăm câmpul în ghilimele.
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = source.map((emp) =>
      [
        `${emp.user.firstName} ${emp.user.lastName}`,
        emp.user.email,
        emp.jobTitle || '',
        emp.department?.name || '',
        emp.company?.name || '',
      ]
        .map(esc)
        .join(',')
    );
    // BOM UTF-8 ca Excel să recunoască diacriticele românești corect.
    const csv = '﻿' + [headers.map(esc).join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `angajati_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    showToast(t('employees.exportDone', { count: source.length }), 'success');
  };

  const openAction = (id, mode) => setActionTarget({ id, mode });

  const confirmAction = async () => {
    if (!actionTarget) return;
    setDeleting(true);
    try {
      if (actionTarget.mode === 'delete') {
        await employeesAPI.delete(actionTarget.id);
        setEmployees(employees.filter((e) => e.id !== actionTarget.id));
        showToast(t('employees.deletedToast'), 'success');
      } else if (actionTarget.mode === 'deactivate') {
        await employeesAPI.deactivate(actionTarget.id);
        setEmployees(
          employees.map((e) =>
            e.id === actionTarget.id ? { ...e, user: { ...e.user, active: false } } : e
          )
        );
        showToast(t('employees.deactivatedToast'), 'success');
      } else if (actionTarget.mode === 'activate') {
        await employeesAPI.activate(actionTarget.id);
        setEmployees(
          employees.map((e) =>
            e.id === actionTarget.id ? { ...e, user: { ...e.user, active: true } } : e
          )
        );
        showToast(t('employees.activatedToast'), 'success');
      }
      setActionTarget(null);
    } catch (err) {
      showToast(err.response?.data?.error || t('employees.actionError'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Helper: numele departamentelor relevante pentru un angajat. Include departamentul direct
  // (membership) + departamentele manageriate (CLM). Astfel CLM-ii apar și în dropdown-ul de
  // departament, și în filtrare — anterior erau excluși dacă aveau `departmentId=null`.
  const empDeptNames = (emp) => {
    const names = [];
    if (emp.department?.name) names.push(emp.department.name);
    if (Array.isArray(emp.managedDepartments)) {
      for (const d of emp.managedDepartments) if (d.name) names.push(d.name);
    }
    return names;
  };

  // Liste pentru dropdown-uri (extrase din angajații curenți)
  const departmentOptions = [
    { value: '', label: t('employees.allDepartments') },
    ...Array.from(new Set(employees.flatMap(empDeptNames)))
      .sort()
      .map((d) => ({ value: d, label: d })),
  ];
  const jobTitleOptions = [
    { value: '', label: t('employees.allJobs') },
    ...Array.from(
      new Set(
        employees
          .filter((e) => !selectedDepartment || empDeptNames(e).includes(selectedDepartment))
          .map((e) => e.jobTitle)
          .filter(Boolean)
      )
    )
      .sort()
      .map((j) => ({ value: j, label: j })),
  ];
  const companyOptions = [
    { value: '', label: t('employees.allCompanies') },
    ...companies.map((c) => ({ value: c.id, label: c.name })),
  ];

  const filteredEmployees = employees.filter((emp) => {
    const name = `${emp.user.firstName} ${emp.user.lastName}`.toLowerCase();
    const email = emp.user.email.toLowerCase();
    const s = searchTerm.toLowerCase();
    if (s && !name.includes(s) && !email.includes(s)) return false;
    // Filtru departament: include și membership-ul direct, și departamentele manageriate (CLM).
    if (selectedDepartment && !empDeptNames(emp).includes(selectedDepartment)) return false;
    if (selectedJobTitle && emp.jobTitle !== selectedJobTitle) return false;
    if (onlyEPI && !emp.hasEPI) return false;
    return true;
  });

  const totalFiltered = filteredEmployees.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const pagedEmployees = filteredEmployees.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const epiCount = employees.filter((e) => e.hasEPI).length;
  const hasActiveFilters = !!(
    searchTerm ||
    (selectedCompany && !companyLocked) ||
    selectedDepartment ||
    selectedJobTitle ||
    onlyEPI
  );
  const clearFilters = () => {
    setSearchTerm('');
    if (!companyLocked) setSelectedCompany('');
    setSelectedDepartment('');
    setSelectedJobTitle('');
    setOnlyEPI(false);
  };

  const colCount = (companyIdFilter ? 5 : 6) + (canManageIP ? 1 : 0) + (selectionMode ? 1 : 0);

  const formatIntervalShort = (days) => {
    if (!days) return null;
    if (days % 365 === 0) return `${days / 365}A`;
    if (days % 30 === 0) return `${days / 30}L`;
    return `${days}z`;
  };
  const IPCellBadges = ({ emp }) => {
    const s = formatIntervalShort(emp.ipSSMIntervalDays);
    const u = formatIntervalShort(emp.ipSUIntervalDays);
    if (!s && !u) return <span className="text-xs text-slate-300 dark:text-slate-600">—</span>;
    return (
      <div className="flex gap-1">
        {s && (
          <span className="inline-flex items-center text-[10px] font-semibold bg-blue-50 dark:bg-slate-800/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-700 rounded px-1.5 py-0.5">
            SSM {s}
          </span>
        )}
        {u && (
          <span className="inline-flex items-center text-[10px] font-semibold bg-orange-50 dark:bg-slate-800/60 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-slate-700 rounded px-1.5 py-0.5">
            SU {u}
          </span>
        )}
      </div>
    );
  };

  const pagedIds = pagedEmployees.map((e) => e.id);
  const allOnPageSelected = pagedIds.length > 0 && pagedIds.every((id) => selectedIds.has(id));

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton variant="title" />
        <Skeleton variant="card" className="h-16" />
        <Skeleton variant="table-row" count={6} />
      </div>
    );
  }

  const actionButtons = (
    <div className="flex gap-2">
      {canManageIP && !selectionMode && (
        <Button variant="secondary" size="md" onClick={enterSelectionMode}>
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {t('employees.bulkIP')}
        </Button>
      )}
      <Button
        variant="secondary"
        size="md"
        onClick={handleExportCSV}
        disabled={filteredEmployees.length === 0}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        {t('employees.exportCsv')}
      </Button>
      <Button
        variant="secondary"
        size="md"
        onClick={() => {
          setShowImportModal(true);
          setImportResult(null);
          setImportFile(null);
        }}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
          />
        </svg>
        {t('employees.importExcel')}
      </Button>
      <Link to={companyIdFilter ? `/employees/new?companyId=${companyIdFilter}` : '/employees/new'}>
        <Button variant="primary" size="md">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {t('employees.addEmployee')}
        </Button>
      </Link>
    </div>
  );

  return (
    <div className="space-y-5">
      {embedded ? (
        <div className="flex justify-end">{actionButtons}</div>
      ) : (
        <PageHeader
          title={companyName ? `${t('employees.title')} — ${companyName}` : t('employees.title')}
          subtitle={t('employees.countOfTotal', {
            filtered: filteredEmployees.length,
            total: employees.length,
          })}
          actions={actionButtons}
        />
      )}

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {employees.length === 0 && !error ? (
        <EmptyState
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
              />
            </svg>
          }
          title={t('employees.noEmployeesTitle')}
          description={t('employees.addFirstDesc')}
          actionLabel={t('employees.addEmployee')}
          onAction={() =>
            navigate(
              companyIdFilter ? `/employees/new?companyId=${companyIdFilter}` : '/employees/new'
            )
          }
        />
      ) : (
        <>
          {/* Filtre */}
          <Card>
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${isSSMRole ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}
            >
              <Input
                placeholder={t('employees.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {isSSMRole &&
                (companyLocked ? (
                  <div
                    className="input bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed flex items-center px-3"
                    title={t('employees.fixedCompanyTitle')}
                  >
                    {companyName || t('employees.currentCompany')}
                  </div>
                ) : (
                  <SearchableSelect
                    value={selectedCompany}
                    onChange={(v) => {
                      setSelectedCompany(v);
                      setSelectedDepartment('');
                      setSelectedJobTitle('');
                    }}
                    options={companyOptions}
                    placeholder={t('employees.searchCompany')}
                  />
                ))}
              <SearchableSelect
                value={selectedDepartment}
                onChange={(v) => {
                  setSelectedDepartment(v);
                  setSelectedJobTitle('');
                }}
                options={departmentOptions}
                placeholder={t('employees.searchDept')}
                disabled={departmentOptions.length <= 1}
              />
              <SearchableSelect
                value={selectedJobTitle}
                onChange={setSelectedJobTitle}
                options={jobTitleOptions}
                placeholder={t('employees.searchJob')}
                disabled={jobTitleOptions.length <= 1}
              />
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyEPI}
                  onChange={(e) => setOnlyEPI(e.target.checked)}
                  className="h-4 w-4 text-danger-600 rounded"
                />
                <span className="text-xs font-medium text-danger-700 dark:text-danger-300">
                  {t('employees.onlyPPE')}
                </span>
              </label>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {t('employees.countOfTotal', {
                  filtered: filteredEmployees.length,
                  total: employees.length,
                })}
                {' · '}
                <span className="text-danger-600 dark:text-danger-300">{epiCount}</span>{' '}
                {t('employees.epi')}
              </span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-danger-500 dark:text-danger-300 hover:text-danger-700 ml-auto"
                >
                  {t('employees.clearFilters')}
                </button>
              )}
            </div>
          </Card>

          {/* Banner instrucțiuni mod selecție */}
          {selectionMode && (
            <div className="sticky top-14 z-20 bg-primary-50 dark:bg-primary-950/50 border-2 border-primary-500 dark:border-primary-700 rounded-lg overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    {selectedIds.size}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-primary-900 dark:text-primary-200">
                      {selectedIds.size === 0
                        ? t('employees.checkEmployeesToEdit')
                        : selectedIds.size === 1
                          ? t('employees.selectedSingular', { count: selectedIds.size })
                          : t('employees.selectedPlural', { count: selectedIds.size })}
                    </p>
                    <p className="text-xs text-primary-700 dark:text-primary-300">
                      {selectedIds.size === 0
                        ? t('employees.selectionFilterHint')
                        : t('employees.selectionReadyHint')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={selectedIds.size === 0}
                    onClick={() => setBulkIPModalOpen(true)}
                  >
                    {t('employees.setIPInterval')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={exitSelectionMode}>
                    {t('employees.giveUp')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <Card padding={false}>
            {/* MOBILE: carduri (sub md). Pentru fiecare angajat, o card cu avatar, nume, funcție,
                badges, departament, plus butoane wrap-uite. */}
            <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {filteredEmployees.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-500 dark:text-slate-400">
                  {t('employees.notFoundFor', { term: searchTerm })}
                </p>
              ) : (
                pagedEmployees.map((emp) => (
                  <div
                    key={`mob-${emp.id}`}
                    className={`p-4 ${selectionMode && selectedIds.has(emp.id) ? 'bg-primary-50/60 dark:bg-primary-950/50' : ''} ${selectionMode ? 'cursor-pointer' : ''}`}
                    onClick={selectionMode ? () => toggleSelected(emp.id) : undefined}
                  >
                    <div className="flex items-start gap-3">
                      {selectionMode && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(emp.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelected(emp.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 mt-1 text-primary-600 rounded flex-shrink-0"
                        />
                      )}
                      <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center text-primary-700 dark:text-primary-300 font-semibold text-sm flex-shrink-0">
                        {emp.user.firstName[0]}
                        {emp.user.lastName[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/employees/${emp.id}`}
                          className="block text-sm font-semibold text-slate-900 dark:text-slate-100 hover:text-primary-600 truncate"
                        >
                          {emp.user.firstName} {emp.user.lastName}
                        </Link>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {emp.user.email}
                        </p>
                        <p className="text-xs text-slate-700 dark:text-slate-300 mt-1">
                          {emp.jobTitle}
                        </p>
                        <div className="flex flex-wrap items-center gap-1 mt-1.5">
                          <RoleBadge role={emp.user.role} />
                          {emp.isCLM && (
                            <Badge color="purple" className="text-[10px] font-bold">
                              CLM
                            </Badge>
                          )}
                          {emp.hasEPI && (
                            <Badge color="red" className="text-[10px] font-bold">
                              EPI
                            </Badge>
                          )}
                          {emp.user.isSSMInstructor && (
                            <Badge color="blue" className="text-[10px]">
                              SSM
                            </Badge>
                          )}
                          {emp.user.isCadruTehnicPSI && (
                            <Badge color="orange" className="text-[10px]">
                              PSI
                            </Badge>
                          )}
                          {(emp.user.canManageEmployees || emp.department?.isHR) && (
                            <Badge color="green" className="text-[10px]">
                              HR
                            </Badge>
                          )}
                        </div>
                        {(() => {
                          const hasDept = !!emp.department?.name;
                          const hasManaged =
                            emp.managedDepartments && emp.managedDepartments.length > 0;
                          const showCompany = emp.company?.name && !companyIdFilter;
                          const showUnassignedTag = !hasDept && !hasManaged && showUnassigned;
                          if (!hasDept && !hasManaged && !showCompany && !showUnassignedTag)
                            return null;
                          return (
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
                              {hasDept ? (
                                emp.department.name
                              ) : hasManaged ? (
                                <span>{emp.managedDepartments.map((d) => d.name).join(', ')}</span>
                              ) : showUnassignedTag ? (
                                <span className="text-amber-600 dark:text-amber-400">
                                  {t('employees.unassigned')}
                                </span>
                              ) : null}
                              {showCompany && (
                                <span>
                                  {hasDept || hasManaged || showUnassignedTag ? ' · ' : ''}
                                  {emp.company.name}
                                </span>
                              )}
                            </p>
                          );
                        })()}
                        {canManageIP && (
                          <div className="mt-1.5">
                            <IPCellBadges emp={emp} />
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Acțiuni grupate jos cu wrap, gap mic */}
                    {!selectionMode && (
                      <div className="flex flex-wrap items-center gap-1 mt-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                        <Link to={`/employees/${emp.id}`}>
                          <Button variant="ghost" size="sm">
                            {t('employees.view')}
                          </Button>
                        </Link>
                        <Link to={`/employees/${emp.id}/edit`}>
                          <Button variant="ghost" size="sm">
                            {t('employees.editAction')}
                          </Button>
                        </Link>
                        {user?.role === 'SUPER_ADMIN' &&
                          emp.user?.active &&
                          emp.user?.role !== 'SUPER_ADMIN' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-amber-700"
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    t('impersonation.confirmStart', {
                                      name: `${emp.user.firstName} ${emp.user.lastName}`,
                                    })
                                  )
                                )
                                  return;
                                const res = await impersonate(emp.userId);
                                if (!res.success) showToast(res.error, 'error');
                              }}
                            >
                              {t('impersonation.buttonLabel')}
                            </Button>
                          )}
                        {emp.user?.active === false ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-700"
                            onClick={() => openAction(emp.id, 'activate')}
                          >
                            {t('employees.activateAction')}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-amber-600"
                            onClick={() => openAction(emp.id, 'deactivate')}
                          >
                            {t('employees.deactivateAction')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-danger-600"
                          onClick={() => openAction(emp.id, 'delete')}
                        >
                          {t('employees.deleteAction')}
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* DESKTOP: tabelul existent — vizibil doar de la md+ */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    {selectionMode && (
                      <th className="table-header w-8">
                        <input
                          type="checkbox"
                          checked={allOnPageSelected}
                          onChange={() => togglePageSelection(pagedIds, allOnPageSelected)}
                          className="h-4 w-4 text-primary-600 rounded"
                          aria-label={t('employees.selectAllAria')}
                        />
                      </th>
                    )}
                    <th className="table-header">{t('employees.colName')}</th>
                    <th className="table-header hidden md:table-cell">{t('employees.colEmail')}</th>
                    <th className="table-header">{t('employees.colJobRole')}</th>
                    <th className="table-header hidden sm:table-cell">{t('employees.colDept')}</th>
                    {!companyIdFilter && (
                      <th className="table-header hidden md:table-cell">
                        {t('employees.colCompany')}
                      </th>
                    )}
                    {canManageIP && (
                      <th className="table-header hidden lg:table-cell">
                        {t('employees.colIPInterval')}
                      </th>
                    )}
                    <th className="table-header text-right">{t('employees.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className="table-cell text-center py-10">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {t('employees.notFoundFor', { term: searchTerm })}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    pagedEmployees.map((emp) => (
                      <tr
                        key={emp.id}
                        className={`table-row ${selectionMode && selectedIds.has(emp.id) ? 'bg-primary-50/60 dark:bg-primary-950/50' : ''} ${selectionMode ? 'cursor-pointer' : ''}`}
                        onClick={selectionMode ? () => toggleSelected(emp.id) : undefined}
                      >
                        {selectionMode && (
                          <td className="table-cell w-8" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(emp.id)}
                              onChange={() => toggleSelected(emp.id)}
                              className="h-4 w-4 text-primary-600 rounded"
                              aria-label={t('employees.selectEmpAria', {
                                name: `${emp.user.firstName} ${emp.user.lastName}`,
                              })}
                            />
                          </td>
                        )}
                        <td className="table-cell whitespace-nowrap">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center text-primary-700 dark:text-primary-300 font-semibold text-xs flex-shrink-0">
                              {emp.user.firstName[0]}
                              {emp.user.lastName[0]}
                            </div>
                            <Link
                              to={`/employees/${emp.id}`}
                              className="text-sm font-medium text-slate-900 dark:text-slate-100 hover:text-primary-600"
                            >
                              {emp.user.firstName} {emp.user.lastName}
                            </Link>
                          </div>
                        </td>
                        <td className="table-cell whitespace-nowrap text-sm text-slate-500 dark:text-slate-400 hidden md:table-cell">
                          {emp.user.email}
                        </td>
                        <td className="table-cell whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span className="text-sm text-slate-900 dark:text-slate-100">
                              {emp.jobTitle}
                            </span>
                            <div className="flex items-center gap-1 flex-wrap">
                              <RoleBadge role={emp.user.role} />
                              {emp.isCLM && (
                                <Badge color="purple" className="text-[10px] font-bold">
                                  CLM
                                </Badge>
                              )}
                              {emp.hasEPI && (
                                <Badge color="red" className="text-[10px] font-bold">
                                  EPI
                                </Badge>
                              )}
                              {emp.user.isSSMInstructor && (
                                <Badge color="blue" className="text-[10px]">
                                  SSM
                                </Badge>
                              )}
                              {emp.user.isCadruTehnicPSI && (
                                <Badge color="orange" className="text-[10px]">
                                  PSI
                                </Badge>
                              )}
                              {(emp.user.canManageEmployees || emp.department?.isHR) && (
                                <Badge color="green" className="text-[10px]">
                                  HR
                                </Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="table-cell whitespace-nowrap hidden sm:table-cell">
                          {emp.department ? (
                            <span className="text-sm text-slate-600 dark:text-slate-300">
                              {emp.department.name}
                            </span>
                          ) : emp.managedDepartments && emp.managedDepartments.length > 0 ? (
                            <span className="text-sm text-slate-600 dark:text-slate-300">
                              {emp.managedDepartments.map((d) => d.name).join(', ')}
                            </span>
                          ) : showUnassigned ? (
                            <Badge color="yellow">{t('employees.unassigned')}</Badge>
                          ) : (
                            <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
                          )}
                        </td>
                        {!companyIdFilter && (
                          <td className="table-cell whitespace-nowrap hidden md:table-cell">
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              {emp.company?.name || '—'}
                            </span>
                          </td>
                        )}
                        {canManageIP && (
                          <td className="table-cell whitespace-nowrap hidden lg:table-cell">
                            <IPCellBadges emp={emp} />
                          </td>
                        )}
                        <td className="table-cell whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Link to={`/employees/${emp.id}`}>
                              <Button variant="ghost" size="sm">
                                {t('employees.view')}
                              </Button>
                            </Link>
                            <Link to={`/employees/${emp.id}/edit`}>
                              <Button variant="ghost" size="sm">
                                {t('employees.editAction')}
                              </Button>
                            </Link>
                            {user?.role === 'SUPER_ADMIN' &&
                              emp.user?.active &&
                              emp.user?.role !== 'SUPER_ADMIN' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-amber-700 hover:text-amber-800"
                                  title={t('impersonation.buttonTitle')}
                                  onClick={async () => {
                                    if (
                                      !window.confirm(
                                        t('impersonation.confirmStart', {
                                          name: `${emp.user.firstName} ${emp.user.lastName}`,
                                        })
                                      )
                                    )
                                      return;
                                    const res = await impersonate(emp.userId);
                                    if (!res.success) showToast(res.error, 'error');
                                  }}
                                >
                                  {t('impersonation.buttonLabel')}
                                </Button>
                              )}
                            {emp.user?.active === false ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-700 hover:text-green-800"
                                onClick={() => openAction(emp.id, 'activate')}
                              >
                                {t('employees.activateAction')}
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-600 hover:text-amber-700"
                                onClick={() => openAction(emp.id, 'deactivate')}
                              >
                                {t('employees.deactivateAction')}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-danger-600 hover:text-danger-700"
                              onClick={() => openAction(emp.id, 'delete')}
                            >
                              {t('employees.deleteAction')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalFiltered}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Modal Bulk IP Intervals */}
      <Modal
        open={bulkIPModalOpen}
        onClose={() => setBulkIPModalOpen(false)}
        title={t('employees.bulkIPFor', { count: selectedIds.size })}
        size="md"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="secondary" onClick={() => setBulkIPModalOpen(false)}>
              {t('employees.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={submitBulkIP}
              loading={bulkSaving}
              disabled={!bulkApplySSM && !bulkApplySU}
            >
              {t('employees.apply')}
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <p className="text-sm text-slate-600 dark:text-slate-300">{t('employees.bulkIPHint')}</p>

          <div
            className={`border rounded-lg p-4 space-y-3 ${canEditSSMField ? 'border-slate-200 dark:border-slate-800' : 'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800'}`}
          >
            <label
              className={`flex items-center gap-2 ${canEditSSMField ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
            >
              <input
                type="checkbox"
                checked={bulkApplySSM && canEditSSMField}
                disabled={!canEditSSMField}
                onChange={(e) => setBulkApplySSM(e.target.checked)}
                className="h-4 w-4 text-primary-600 rounded"
              />
              <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                {t('employees.modifyIPSSM')}
              </span>
              {!canEditSSMField && (
                <span className="text-[10px] text-slate-500 dark:text-slate-400 ml-auto">
                  {t('employees.requiresSSMInstructor')}
                </span>
              )}
            </label>
            {bulkApplySSM && canEditSSMField && (
              <div className="pl-6">
                <IntervalPicker value={bulkSSM} onChange={setBulkSSM} />
              </div>
            )}
          </div>

          <div
            className={`border rounded-lg p-4 space-y-3 ${canEditSUField ? 'border-slate-200 dark:border-slate-800' : 'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800'}`}
          >
            <label
              className={`flex items-center gap-2 ${canEditSUField ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
            >
              <input
                type="checkbox"
                checked={bulkApplySU && canEditSUField}
                disabled={!canEditSUField}
                onChange={(e) => setBulkApplySU(e.target.checked)}
                className="h-4 w-4 text-primary-600 rounded"
              />
              <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                {t('employees.modifyIPSU')}
              </span>
              {!canEditSUField && (
                <span className="text-[10px] text-slate-500 dark:text-slate-400 ml-auto">
                  {t('employees.requiresCadruPSI')}
                </span>
              )}
            </label>
            {bulkApplySU && canEditSUField && (
              <div className="pl-6">
                <IntervalPicker value={bulkSU} onChange={setBulkSU} />
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                  {t('employees.ppeTip')}
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal Import Bulk */}
      <Modal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title={t('employees.importTitle')}
        size="lg"
        footer={
          !importResult ? (
            <div className="flex justify-end gap-3 w-full">
              <Button variant="secondary" onClick={() => setShowImportModal(false)}>
                {t('employees.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleImport}
                loading={importLoading}
                disabled={!importFile}
              >
                {t('employees.importBtn')}
              </Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button variant="primary" onClick={() => setShowImportModal(false)}>
                {t('employees.closeBtn')}
              </Button>
            </div>
          )
        }
      >
        {!importResult ? (
          <div className="space-y-4">
            {/* #25: descărcarea modelului Excel — acțiune proeminentă (primul pas), sus în modal. */}
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-950/30">
              <p className="text-sm text-primary-900 dark:text-primary-200 font-medium">
                {t('employees.downloadTemplateHint')}
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={handleDownloadTemplate}
                className="flex-shrink-0"
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                {t('employees.downloadTemplate')}
              </Button>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {t('employees.importHint')}
            </p>
            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="block w-full text-sm text-slate-500 dark:text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 dark:file:bg-primary-950/50 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100"
              />
            </div>
            <Alert type="info">
              <p className="text-xs">
                <strong>{t('employees.columnsLabel')}</strong> {t('employees.columnsList')}
                <br />
                <strong>{t('employees.requiredLabel')}</strong> {t('employees.requiredList')}
                <br />
                {t('employees.moreImportHint')}
              </p>
            </Alert>
          </div>
        ) : importResult.error ? (
          <Alert type="error">{importResult.error}</Alert>
        ) : (
          <div className="space-y-4">
            <Alert type="success">
              {t('employees.importFinished', { count: importResult.created })}
            </Alert>
            {importResult.skipped > 0 && (
              <Alert type="warning">
                {t('employees.importSkipped', { count: importResult.skipped })}
              </Alert>
            )}
            {importResult.errors > 0 && (
              <Alert type="error">
                {t('employees.importErrors', { count: importResult.errors })}
              </Alert>
            )}
            {importResult.warnings > 0 && (
              <Alert type="warning">
                {t('employees.importWarnings', { count: importResult.warnings })}
              </Alert>
            )}
            {importResult.autoCreatedDepartments?.length > 0 && (
              <Alert type="info">
                {t('employees.autoDepts', { list: importResult.autoCreatedDepartments.join(', ') })}
              </Alert>
            )}
            {importResult.details?.warnings?.length > 0 && (
              <div className="max-h-48 overflow-y-auto bg-warning-50 dark:bg-warning-950/50 border border-warning-200 dark:border-warning-700 rounded p-2">
                <p className="text-sm font-medium text-warning-800 dark:text-warning-300 mb-2">
                  {t('employees.warningsPerEmp')}
                </p>
                {importResult.details.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-warning-700 dark:text-warning-300">
                    {w.message ||
                      t('employees.rowMissing', {
                        row: w.row,
                        missing: (w.missing || []).join(', '),
                      })}
                  </p>
                ))}
              </div>
            )}
            {importResult.details?.errors?.length > 0 && (
              <div className="max-h-40 overflow-y-auto bg-danger-50 dark:bg-danger-950/50 border border-danger-200 dark:border-danger-700 rounded p-2">
                <p className="text-sm font-medium text-danger-800 dark:text-danger-300 mb-2">
                  {t('employees.errorsDetails')}
                </p>
                {importResult.details.errors.map((err, i) => (
                  <p key={i} className="text-xs text-danger-700 dark:text-danger-300">
                    {t('employees.rowError', { row: err.row, error: err.error })}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={!!actionTarget}
        onClose={() => setActionTarget(null)}
        onConfirm={confirmAction}
        title={
          actionTarget?.mode === 'delete'
            ? t('employees.deleteTitle')
            : actionTarget?.mode === 'activate'
              ? t('employees.activateTitle')
              : t('employees.deactivateTitle')
        }
        message={
          actionTarget?.mode === 'delete'
            ? t('employees.deleteMsg')
            : actionTarget?.mode === 'activate'
              ? t('employees.activateMsg')
              : t('employees.deactivateMsg')
        }
        confirmLabel={
          actionTarget?.mode === 'delete'
            ? t('employees.deleteConfirm')
            : actionTarget?.mode === 'activate'
              ? t('employees.activate')
              : t('employees.deactivate')
        }
        variant={actionTarget?.mode === 'activate' ? 'primary' : 'danger'}
        loading={deleting}
      />
    </div>
  );
};

export default Employees;
